import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Container,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  Tabs,
  Tab,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import BarChartIcon from "@mui/icons-material/BarChart";

import SavingsIcon from "@mui/icons-material/Savings";
import Co2Icon from "@mui/icons-material/Co2";

import useApi from "../../../shared/hooks/useApi";
import {
  getUserInfo,
  getUserSettings,
  updateUserInfo,
  updateUserSettings,
} from "../../utils/userSettings";
import {
  handleAutoLogin,
  checkForEmailParameter,
  getReturnUrl,
} from "../../utils/autoLogin";
import ReactApexChart from "react-apexcharts";
import { Link, useLocation, useNavigate } from "react-router-dom";

const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(4),
  background: "#f4f6fa",
  minHeight: "100vh",
}));

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));


const LeadgenDashboard = ({ settingsProps, setLoading }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  // Helper functions for timezone conversion
  // These ensure all datetime operations use Belgium timezone (UTC+1/UTC+2 with DST)
  const getBelgiumTime = () => {
    const now = new Date();
    return new Date(
      now.toLocaleString("en-US", { timeZone: "Europe/Brussels" })
    );
  };

  const formatBelgiumTime = (datetime) => {
    const dateTime = new Date(datetime);
    return dateTime.toLocaleString("en-US", {
      timeZone: "Europe/Brussels",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // Settings state - will be populated from userSettings
  const [country, setCountry] = useState("BE");
  const [gasPriceType, setGasPriceType] = useState("fixed");
  const [gasPrice, setGasPrice] = useState("");
  const [co2PriceType, setCo2PriceType] = useState("fixed");
  const [co2Price, setCo2Price] = useState("");

  const [eBoilerEff, setEBoilerEff] = useState("95");
  const [gasBoilerEff, setGasBoilerEff] = useState("90");
  const [gridCost, setGridCost] = useState("0.12");

  const [copiedSettings, setCopiedSettings] = useState(false);
  const [dispatchData, setDispatchData] = useState([]);
  const [currentAdvice, setCurrentAdvice] = useState("");

  const [gasDayAheadData, setGasDayAheadData] = useState([]);
  const [electricityDayAheadData, setElectricityDayAheadData] = useState([]);

  const [lastUpdated, setLastUpdated] = useState(null);
  const { apiGet, apiPost } = useApi();
  const [userSettings, setUserSettings] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();
  const [upDown, setUpDown] = useState(true);
  const [apiKey, setApiKey] = useState(null);

  const [apiUrl, setApiUrl] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const [email, setEmail] = useState(null);
  const [state, setState] = useState({
    series: [44, 55],
    options: {
      chart: {
        width: 220,
        type: "pie",
      },
      labels: ["E-Boiler", "Gas Boiler"],
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 220,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    },
  });

  // Google Tag Manager (GTM) integration for this page
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    // Initialize dataLayer if needed
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    // Inject GTM script into <head> if not already present
    if (!document.querySelector('script[data-gtm-id="GTM-TPVZR8H"]')) {
      const gtmScript = document.createElement("script");
      gtmScript.async = true;
      gtmScript.src =
        "https://www.googletagmanager.com/gtm.js?id=GTM-TPVZR8H&l=dataLayer";
      gtmScript.setAttribute("data-gtm-id", "GTM-TPVZR8H");
      document.head.appendChild(gtmScript);
    }

    // Inject <noscript> iframe into <body> (as React equivalent of body snippet)
    if (!document.querySelector('noscript[data-gtm-id="GTM-TPVZR8H"]')) {
      const gtmNoScript = document.createElement("noscript");
      gtmNoScript.setAttribute("data-gtm-id", "GTM-TPVZR8H");
      gtmNoScript.innerHTML =
        '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-TPVZR8H" height="0" width="0" style="display:none;visibility:hidden"></iframe>';
      document.body.insertBefore(gtmNoScript, document.body.firstChild);
    }
  }, []);

  // Debug current Belgium time
  useEffect(() => {
    const logBelgiumTime = () => {
      const now = new Date();
      const belgiumTime = getBelgiumTime();
    };

    logBelgiumTime();
    const interval = setInterval(logBelgiumTime, 60000); // Log every minute

    return () => clearInterval(interval);
  }, []);

  // Build pieOptions when dispatchData updates to avoid stale state
  useEffect(() => {
    if (!Array.isArray(dispatchData)) return;
    const eHours = dispatchData.filter((d) => d === "e-boiler").length;
    const gHours = dispatchData.filter((d) => d === "gas-boiler").length;
    const total = eHours + gHours;
    const series = total > 0 ? [eHours, gHours] : [1, 1];
    const ePercent = total > 0 ? Math.round((eHours / total) * 100) : 0;

    //get old state update series part
    setState({
      ...state,
      series: series,
    });
  }, [dispatchData]);

  // Update social stats to use savings ratios
  const [socialStats, setSocialStats] = useState([
    {
      icon: <SavingsIcon sx={{ fontSize: 24 }} />,
      label: "Net Savings / week",
      value: 0,
      color: "#6366f1",
      bgcolor: "rgba(99, 102, 241, 0.1)",
      currency: "€",
    },
    /*   {
      icon: <QueryBuilderIcon sx={{ fontSize: 24 }} />,
      label: "E-Boiler Operating Hours / week",
      value: 0,
      color: "#0ea5e9",
      bgcolor: "rgba(14, 165, 233, 0.1)",
      currency: "h",
    }, */
    {
      icon: <Co2Icon sx={{ fontSize: 24 }} />,
      label: "CO₂ Saved / week",
      value: 0,
      color: "#10b981",
      bgcolor: "rgba(16, 185, 129, 0.1)",
      currency: "t",
    },
  ]);

  // Create chart configuration based on current data
  const createChartConfig = () => {
    // Use electricity data as the base timeline since it has the most granular data
    const baseTimePoints = electricityDayAheadData.map((item) => {
      return formatBelgiumTime(item.datetime);
    });

    // Create data mapping for each series using the base timeline
    const electricityDataMap = new Map();
    electricityDayAheadData.forEach((item) => {
      const timeKey = formatBelgiumTime(item.datetime);
      electricityDataMap.set(timeKey, item.value);
    });

    const gasDataMap = new Map();
    gasDayAheadData.forEach((item) => {
      const timeKey = formatBelgiumTime(item.datetime);
      gasDataMap.set(timeKey, item.value);
    });

    // Calculate minimum value from all data to position dots at the bottom
    const allValues = [
      ...electricityDayAheadData.map(item => item.value),
      ...gasDayAheadData.map(item => item.value)
    ].filter(val => val !== null && val !== undefined);
    const minValue = allValues.length > 0 ? Math.min(...allValues) : 0;
    const dotOffset = (minValue < 0) ? minValue - Math.abs(minValue) * 0.1 : minValue * 0.1;

    const marketShareChartOptions = {
      chart: {
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      xaxis: {
        categories: baseTimePoints,

        labels: {
          formatter: function (value, timestamp, opts) {
            // Check if value is a string before calling split
            if (typeof value !== 'string') {
              return "";
            }
            
            // Parse format: "Aug 30, 20:00"
            const [datePart, timePart] = value.split(", ");
            const hour = parseInt((timePart || "").split(":")[0], 10);
            
            // Only show date labels at day transitions (hour 00:00)
            if (hour === 0) {
              return datePart; // Return just the date part (e.g., "Aug 30")
            }
            return ""; // Hide other time labels
          },
          style: {
            colors: baseTimePoints.map((timePoint) => {
              // Parse format: "Aug 30, 20:00"
              const [datePart, timePart] = timePoint.split(", ");
              const [month, dayStr] = (datePart || "").split(" ");
              const hour = parseInt((timePart || "").split(":")[0], 10);
              const currentDate = new Date();
              const currentDay = currentDate.getDate();
              const currentMonth = currentDate.toLocaleDateString("en-US", {
                month: "short",
              });
              const currentHour = currentDate.getHours();
              const isCurrentDay =
                month === currentMonth && parseInt(dayStr, 10) === currentDay;
              const isCurrentHour = hour === currentHour;
              return isCurrentDay && isCurrentHour ? "#ed6c02" : "#666";
            }),
          },
          rotate: -45,
        },
      },
      yaxis: {
        title: {
          text: "Price (€/MWh)",
        },
        labels: {
          formatter: function (value) {
            return value.toFixed(2);
          },
        },
        min: undefined,
        max: undefined,
      },
      stroke: {
        curve: "stepline",
        width: 3,
      },
      markers: {
        size: 0,
      },
      plotOptions: {
        scatter: {
          markers: {
            size: 8,
            strokeWidth: 2,
          },
        },
      },
      colors: ["#8b5cf6", "#10b981", "#0ea5e9", "#f59e0b"],
      legend: {
        position: "top",
        horizontalAlign: "right",
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          show: true,
          formatter: function (value, { seriesIndex, dataPointIndex, w }) {
            // Get the actual time point from baseTimePoints using dataPointIndex
            if (dataPointIndex !== undefined && baseTimePoints[dataPointIndex]) {
              return baseTimePoints[dataPointIndex]; // This will show the full timePoint string (e.g., "Sep 9, 20:00")
            }
            return "";
          },
        },
        y: {
          formatter: function (value, { seriesIndex, dataPointIndex }) {
            if (seriesIndex === 2) {
              // For the E-Boiler dispatch series
              return "E-Boiler";
            }
            if (seriesIndex === 3) {
              // For the Gas Boiler dispatch series
              return "Gas Boiler";
            }
            return value ? `€${value.toFixed(2)}/MWh` : "N/A";
          },
        },
      },
      annotations: {
        xaxis: (() => {
          // Find the current time point in baseTimePoints
          const currentDate = new Date();
          const currentDay = currentDate.getDate();
          const currentMonth = currentDate.toLocaleDateString("en-US", {
            month: "short",
          });
          const currentHour = currentDate.getHours();

          // Find matching time point
          const currentTimePoint = baseTimePoints.find((timePoint) => {
            // Parse format: "Aug 30, 20:00"
            const [datePart, timePart] = timePoint.split(", ");
            const [month, dayStr] = (datePart || "").split(" ");
            const hour = parseInt((timePart || "").split(":")[0], 10);
            return (
              month === currentMonth &&
              parseInt(dayStr, 10) === currentDay &&
              hour === currentHour
            );
          });

          if (currentTimePoint && baseTimePoints.length > 0) {
            return [
              {
                x: currentTimePoint,
                borderColor: "#ed6c02",
                borderWidth: 3,
                label: {
                  text: "Current Time",
                  style: {
                    color: "#fff",
                    background: "#f59e0b",
                    fontSize: "12px",
                    fontWeight: "bold",
                  },
                },
              },
            ];
          }
          return [];
        })(),
      },
    };

    const marketShareChartSeries = [
      {
        name: "Electricity Day-Ahead Price",
        data: baseTimePoints.map((timePoint) => {
          const value = electricityDataMap.get(timePoint);
          return value !== undefined ? value : null;
        }),
        color: "#3b82f6", // Blue for electricity price
      },
      {
        name: "Gas Day-Ahead Price",
        data: baseTimePoints.map((timePoint) => {
          const value = gasDataMap.get(timePoint);
          return value !== undefined ? value : null;
        }),
      },
      {
        name: "E-Boiler Dispatch",
        data: baseTimePoints.map((timePoint, idx) => {
          const boilerType = dispatchData[idx] || "e-boiler";
          const isGasBoiler = boilerType === "gas-boiler";
          // Return value at the bottom of the chart
          return isGasBoiler ? null : dotOffset;
        }),
        type: "scatter",
        color: "#0ea5e9", // Sky blue for e-boiler
      },
      {
        name: "Gas Boiler Dispatch",
        data: baseTimePoints.map((timePoint, idx) => {
          const boilerType = dispatchData[idx] || "e-boiler";
          const isGasBoiler = boilerType === "gas-boiler";
          // Return value slightly below the e-boiler dots
          return isGasBoiler ? dotOffset - Math.abs(dotOffset) * 0.1 : null;
        }),
        type: "scatter",
        color: "#f59e0b", // Amber for gas boiler
      },
    ];

    return { marketShareChartOptions, marketShareChartSeries };
  };

  const handleCopySettings = () => {
    const settingsPayload = {
      country: country,
      gas_price: {
        mode: gasPriceType,
        value: gasPriceType === "fixed" ? parseFloat(gasPrice) || 30 : null,
      },
      co2_price: {
        mode: co2PriceType,
        value: co2PriceType === "fixed" ? parseFloat(co2Price) || 75 : null,
      },
      e_boiler_efficiency: parseFloat(eBoilerEff) || 95,
      gas_boiler_efficiency: parseFloat(gasBoilerEff) || 90,
      grid_cost: parseFloat(gridCost) || 0.12,
      email: userInfo?.email || "user@example.com",
    };

    navigator.clipboard.writeText(JSON.stringify(settingsPayload, null, 2));
    setCopiedSettings(true);
    setTimeout(() => setCopiedSettings(false), 1500);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };



  const handleMakeApiRequest = async () => {
    if (!apiUrl || !apiKey) {
      setRequestError("API endpoint or API key is not configured");
      return;
    }

    setIsLoadingRequest(true);
    setRequestError(null);
    setApiResponse(null);

    try {
      const payload = {
        country: country,
        gas_price: {
          mode: gasPriceType,
          value: gasPriceType === "fixed" ? parseFloat(gasPrice) || 30 : null,
        },
        co2_price: {
          mode: co2PriceType,
          value: co2PriceType === "fixed" ? parseFloat(co2Price) || 75 : null,
        },
        e_boiler_efficiency: parseFloat(eBoilerEff) || 95,
        gas_boiler_efficiency: parseFloat(gasBoilerEff) || 90,
        grid_cost: parseFloat(gridCost) || 0.12,
        email: userInfo?.email || "user@example.com",
      };
      const fullPayload = {
        payload: payload,
        api_key: apiKey,
      };

      const response = await apiPost(
        "UX_HTTP_API",
        "/esid-25-013/setpoint",
        fullPayload
      );

      const responseData = response;

      setApiResponse({
        status: response.status,
        statusText: response.statusText,
        data: responseData, // Use the full response data directly
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      setRequestError(
        error.message || "An error occurred while making the request"
      );
    } finally {
      setIsLoadingRequest(false);
    }
  };

  useEffect(() => {
    const settings = getUserSettings();
    const userInfo = getUserInfo();
    if (!settings) {
      const newSettings = {
        country: "BE",
        gasPriceType: "fixed",
        gasPrice: "30",
        co2PriceType: "fixed",
        co2Price: "75",
        eBoilerEff: "95",
        gasBoilerEff: "90",
        gridCost: "0.12",
      };
      updateUserSettings(newSettings);
      setUserSettings(newSettings);

      // Set all state variables from default settings
      setCountry(newSettings.country);
      setGasPriceType(newSettings.gasPriceType);
      setGasPrice(newSettings.gasPrice);
      setCo2PriceType(newSettings.co2PriceType);
      setCo2Price(newSettings.co2Price);
      setEBoilerEff(newSettings.eBoilerEff);
      setGasBoilerEff(newSettings.gasBoilerEff);
      setGridCost(newSettings.gridCost);
    } else {
      setUserSettings(settings);

      // Set all state variables from loaded settings
      setCountry(settings.country || "BE");
      setGasPriceType(settings.gasPriceType || "fixed");
      setGasPrice(settings.gasPrice || "30");
      setCo2PriceType(settings.co2PriceType || "fixed");
      setCo2Price(settings.co2Price || "75");
      setEBoilerEff(settings.eBoilerEff || "95");
      setGasBoilerEff(settings.gasBoilerEff || "90");
      setGridCost(settings.gridCost || "0.12");
    }
    if (!userInfo) {
      const newUserInfo = {
        email: "user@example.com",
        user_id: "1234567890",
      };
      updateUserInfo(newUserInfo);
      setUserInfo(newUserInfo);
    } else {
      setUserInfo(userInfo);
    }
  }, [settingsProps]);

  // Handle automatic login when email parameter is present
  useEffect(() => {
    const checkAndHandleAutoLogin = async () => {
      const {email, job_title, company, phone, name} = checkForEmailParameter();
      if (email) {
        try {
          const result = await handleAutoLogin(email, job_title, company, phone, name);
          if (result.success) {
            // Store the email in localStorage for future reference
            localStorage.setItem("useremail", "leadgen@entras.be");
            // Set up the necessary localStorage items for ESID-25-013 project
            localStorage.setItem("selectedProject", "esid-25-013");
            localStorage.setItem(
              "allowedProjects",
              JSON.stringify(["esid-25-013"])
            );
            localStorage.setItem("all_roles", JSON.stringify(["operator"]));
            localStorage.setItem(
              "allowedPages",
              JSON.stringify(["/esid-25-013/dashboard"])
            );

            // Refresh the page to ensure proper authentication state
            window.location.reload();
          } else {
            console.error("Automatic login failed:", result.message);
            // If auto login fails, redirect to signin page with error
            navigate("/nopage?error=auto-login-failed");
          }
        } catch (error) {
          console.error("Error during automatic login:", error);
          navigate("/nopage?error=auto-login-error");
        }
      }
    };
    const apiKey = localStorage.getItem("api_key");
    setApiKey(apiKey);
    checkAndHandleAutoLogin();
  }, []);

  const fetchData = async (loader) => {
    try {
      if (loader) {
        setLoading(true);
      }

      const getAnalysis = async () => {
     
        const getDataRoute = `/esid-25-013/setpoint`;
        try {
          // Format settings for API call
          const apiSettings = {
            country: country,
            gas_price: {
              mode: gasPriceType,
              value:
                gasPriceType === "fixed" ? parseFloat(gasPrice) || 30 : null,
            },
            co2_price: {
              mode: co2PriceType,
              value: co2PriceType === "fixed" ? parseFloat(co2Price) || 75 : null,
            },
            e_boiler_efficiency: parseFloat(eBoilerEff) || 95,
            gas_boiler_efficiency: parseFloat(gasBoilerEff) || 90,
            grid_cost: parseFloat(gridCost) || 0.12,
            email: email || "user@example.com",
          };

          const fullPayload = {
            payload: apiSettings,
            api_key: apiKey,
          };
          const data = await apiPost("UX_HTTP_API", getDataRoute, fullPayload);

          // Debug info - can be removed after testing

          const urlValue = data.url; // URL is at the top level
          setApiUrl(urlValue);
          setDispatchData(data.optimization.historical_dispatch);
          setCurrentAdvice(data.optimization.current_hour_advice);
          setLastUpdated(
            new Date(data.optimization.calculated_datetime)
          );
          setSocialStats([
            {
              icon: <SavingsIcon sx={{ fontSize: 24 }} />,
              label: "Net Savings / " + electricityDayAheadData?.length + " hours",
              value: data.optimization.summary.net_savings.toFixed(2),
              color: "#7c4dff",
              bgcolor: "rgba(124, 77, 255, 0.1)",
              currency: "€",
            },
            /*     {
                icon: <QueryBuilderIcon sx={{ fontSize: 24 }} />,
                label: "E-Boiler Operating Hours / week",
                value: data.optimization.summary.e_boiler_operating_hours,
                color: "#1976d2",
                bgcolor: "rgba(25, 118, 210, 0.1)",
                currency: "h",
              }, */
            {
              icon: <Co2Icon sx={{ fontSize: 24 }} />,
              label: "CO₂ Saved / " + electricityDayAheadData?.length + " hours",
              value: data.optimization.summary.co2_saved.toFixed(2),
              color: "#2e7d32",
              bgcolor: "rgba(46, 125, 50, 0.1)",
              currency: "t",
            },
          ]);

          // Extract and transform market data from the response
          // Convert all datetime values to Belgium timezone for proper synchronization
          if (data.marketdata) {
            const marketData = data.marketdata;

            // Helper function to convert UTC datetime to Belgium timezone
            const convertToBelgiumTime = (utcDateTime) => {
              const date = new Date(utcDateTime);
              // Belgium is UTC+1 (winter) or UTC+2 (summer) - let JavaScript handle DST automatically
              const belgiumTime = new Date(
                date.toLocaleString("en-US", { timeZone: "Europe/Brussels" })
              );
              return belgiumTime;
            };

            // Helper function to create consistent datetime format for Belgium timezone
            const createBelgiumDateTime = (date, hour = 0) => {
              const belgiumDate = new Date(date);
              belgiumDate.setHours(hour, 0, 0, 0);
              return belgiumDate.toISOString();
            };
            // Transform electricity day-ahead prices
            let electricityData = [];
            if (marketData.electricity_day_ahead_prices) {
              electricityData = Object.entries(
                marketData.electricity_day_ahead_prices
              ).map(([datetime, value]) => {
                // Convert UTC datetime to Belgium timezone
                const belgiumTime = convertToBelgiumTime(datetime);
                const parsedValue = parseFloat(value);
                return {
                  datetime: belgiumTime.toISOString(),
                  value: isNaN(parsedValue) ? 0 : parsedValue,
                };
              });
              setElectricityDayAheadData(electricityData);
            }

            // Handle gas data - use fixed price if gasPriceType is "fixed", otherwise use market data
            if (userSettings?.gasPriceType === "fixed") {
              // Use fixed gas price for all time points
              const fixedGasPrice = parseFloat(gasPrice) || 30;
              
              // Create gas data using the same timeline as electricity data
              let gasData = [];
              if (electricityData.length > 0) {
                gasData = electricityData.map((item) => ({
                  datetime: item.datetime,
                  value: fixedGasPrice,
                }));
              } else {
                // Fallback: create gas data with current time if no electricity data
                const now = new Date();
                const belgiumTime = convertToBelgiumTime(now.toISOString());
                gasData = [{
                  datetime: belgiumTime.toISOString(),
                  value: fixedGasPrice,
                }];
              }
              
              setGasDayAheadData(gasData);
            } else if (marketData.gas_day_ahead_prices) {
              // Use market data when gasPriceType is not "fixed"
              const gasData = [];
              const entries = Object.entries(
                marketData.gas_day_ahead_prices
              ).sort((a, b) => new Date(a[0]) - new Date(b[0]));

              if (entries.length > 0) {
                const [firstDateTime, firstValue] = entries[0];
                const firstBelgiumTime = convertToBelgiumTime(firstDateTime);
                const firstDate = firstBelgiumTime.toISOString().split("T")[0];

                for (let i = 0; i < 6; i++) {
                  const hourDateTime = createBelgiumDateTime(firstDate, i);
                  const customGasValue = parseFloat(firstValue);

                  gasData.push({
                    datetime: hourDateTime,
                    value: customGasValue,
                  });
                }

                // Process each data point - valid until next data point
                for (let i = 0; i < entries.length; i++) {
                  const [currentDateTime, currentValue] = entries[i];
                  const currentBelgiumTime =
                    convertToBelgiumTime(currentDateTime);
                  const currentDate = currentBelgiumTime
                    .toISOString()
                    .split("T")[0];
                  const currentHour = currentBelgiumTime.getHours();

                  // Determine end time for this price
                  let endDateTime;
                  if (i < entries.length - 1) {
                    // Use next data point as end time
                    endDateTime = new Date(entries[i + 1][0]);
                  } else {
                    endDateTime = new Date(currentDateTime);
                    endDateTime.setHours(endDateTime.getHours() + 24);
                  }

                  // Generate hourly data from current time to end time
                  const startDateTime = new Date(currentDateTime);
                  let currentTime = new Date(startDateTime);

                  while (currentTime < endDateTime) {
                    const belgiumTime = convertToBelgiumTime(
                      currentTime.toISOString()
                    );
                    const hourDateTime = belgiumTime.toISOString();

                    const gasValue = isNaN(parseFloat(currentValue))
                      ? 75
                      : parseFloat(currentValue);

                    gasData.push({
                      datetime: hourDateTime,
                      value: gasValue,
                    });
                    // Move to next hour
                    currentTime.setHours(currentTime.getHours() + 1);
                  }
                }
              }

              setGasDayAheadData(gasData);
            } else {
              // Fallback: create gas data with fixed price if no market data
              const fixedGasPrice = parseFloat(gasPrice) || 30;
              let gasData = [];
              if (electricityData.length > 0) {
                gasData = electricityData.map((item) => ({
                  datetime: item.datetime,
                  value: fixedGasPrice,
                }));
              } else {
                // Create gas data with current time if no electricity data
                const now = new Date();
                const belgiumTime = convertToBelgiumTime(now.toISOString());
                gasData = [{
                  datetime: belgiumTime.toISOString(),
                  value: fixedGasPrice,
                }];
              }
              setGasDayAheadData(gasData);
            }
          }
        } catch (error) {
          console.error("Error fetching analysis:", error);
        }
      };

      // Uncomment to test all endpoints
      // testEndpoints();

      await getAnalysis();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async (loader) => {
      if (userSettings && userInfo) {
        await fetchData(loader);
      }
    };

    loadData(true);

    // Set up interval for every 5 minutes
    const intervalId = setInterval(() => {
      loadData(false);
    }, 5 * 60 * 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [
    country,
    gasPriceType,
    gasPrice,
    co2PriceType,
    eBoilerEff,
    gasBoilerEff,
    gridCost,
    userSettings,
    userInfo,
  ]);

  return (
    <Box sx={{ display: "flex", backgroundColor: "rgba(255, 255, 55, 0.1)" }}>
      {/*       <Sidebar />
       */}{" "}
      <Main>
        {/*         <TopBar />
         */}
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          {/* Main Dashboard Tabs */}
          <SectionPaper sx={{ mb: 4 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={3}
            >
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "rgba(25, 118, 210, 0.1)",
                    mr: 2,
                  }}
                >
                  <BarChartIcon sx={{ color: "#0ea5e9", fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Energy Optimization Dashboard
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monitor prices, analyze performance, and configure
                    integrations
                  </Typography>
                </Box>
              </Box>
              {lastUpdated && (
                <Typography variant="caption" color="text.secondary">
                  Last updated: {lastUpdated.toLocaleString()}
                </Typography>
              )}
            </Box>

            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                mb: 3,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  minHeight: 48,
                },
              }}
              variant={isSmall ? "scrollable" : "standard"}
              scrollButtons={isSmall ? "auto" : false}
            >
              <Tab
                label="Optimization Results"
                sx={{
                  color: activeTab === 0 ? "#0ea5e9" : "text.secondary",
                  "&.Mui-selected": {
                    color: "#0ea5e9",
                  },
                }}
              />
              <Tab
                label="API Configuration"
                sx={{
                  color: activeTab === 1 ? "#6366f1" : "text.secondary",
                  "&.Mui-selected": {
                    color: "#6366f1",
                  },
                }}
              />
            </Tabs>

            {activeTab === 0 && (
              <Box>
                <Grid
                  container
                  alignItems="center"
                  justifyContent="space-between"
                  mb={3}
                >
                  <Grid item>
                    <Typography variant="h6" fontWeight={700}>
                      Optimization Results
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Electricity and Gas day-ahead prices with optimization
                      insights
                    </Typography>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: { xs: 2, sm: 0 },
                    mt: 0,
                    mb: 4,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ flexWrap: "wrap", alignItems: "center" }}
                  >
                  
                    
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        width: 260,
                        height: 160,
                        bgcolor: "rgba(124, 77, 255, 0.1)",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "rgba(124, 77, 255, 0.2)",
                      }}
                    >
                      <SavingsIcon 
                        sx={{ 
                          color: "#6366f1", 
                          fontSize: 32, 
                          mb: 1 
                        }} 
                      />
                      <Typography
                        variant="h6"
                        sx={{ color: "#6366f1", fontWeight: 700 }}
                      >
                        {socialStats?.[0]?.value} {socialStats?.[0]?.currency}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Net Savings / {electricityDayAheadData?.length} hours
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        width: 260,
                        height: 160,
                        bgcolor: "rgba(46, 125, 50, 0.1)",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "rgba(46, 125, 50, 0.2)",
                      }}
                    >
                      <Co2Icon 
                        sx={{ 
                          color: "#10b981", 
                          fontSize: 32, 
                          mb: 1 
                        }} 
                      />
                      <Typography
                        variant="h6"
                        sx={{ color: "#10b981", fontWeight: 700 }}
                      >
                        {socialStats?.[1]?.value} {socialStats?.[1]?.currency}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        CO₂ saved / {electricityDayAheadData?.length} hours
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        bgcolor: "rgba(248, 249, 250, 0.8)",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        width: 260,
                        height: 160,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >   
                        Boiler Usage Ratio / {electricityDayAheadData?.length} hours
                      </Typography>
                      
                      {/* Visual Ratio Bar */}
                      <Box
                        sx={{
                          width: "100%",
                          height: 20,
                          backgroundColor: "#e9ecef",
                          borderRadius: 10,
                          overflow: "hidden",
                          position: "relative",
                          mb: 2,
                        }}
                      >
                        {(() => {
                          const eHours = Array.isArray(state.series) ? state.series[0] || 0 : 0;
                          const gHours = Array.isArray(state.series) ? state.series[1] || 0 : 0;
                          const total = eHours + gHours;
                          const ePercent = total > 0 ? (eHours / total) * 100 : 50;
                          const gPercent = total > 0 ? (gHours / total) * 100 : 50;
                          
                          return (
                            <>
                              {/* E-Boiler section */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: 0,
                                  top: 0,
                                  height: "100%",
                                  width: `${ePercent}%`,
                                  backgroundColor: "#0ea5e9",
                                  transition: "width 0.3s ease",
                                }}
                              />
                              {/* Gas Boiler section */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  right: 0,
                                  top: 0,
                                  height: "100%",
                                  width: `${gPercent}%`,
                                  backgroundColor: "#f59e0b",
                                  transition: "width 0.3s ease",
                                }}
                              />
                            </>
                          );
                        })()}
                      </Box>
                      
                      {/* Legend */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 3,
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor: "#0ea5e9",
                            }}
                          />
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            E-Boiler
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              backgroundColor: "#f59e0b",
                            }}
                          />
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            Gas Boiler
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Percentage values */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          mt: 1,
                        }}
                      >
                        {(() => {
                          const eHours = Array.isArray(state.series) ? state.series[0] || 0 : 0;
                          const gHours = Array.isArray(state.series) ? state.series[1] || 0 : 0;
                          const total = eHours + gHours;
                          const ePercent = total > 0 ? Math.round((eHours / total) * 100) : 0;
                          const gPercent = total > 0 ? Math.round((gHours / total) * 100) : 0;
                          
                          return (
                            <>
                              <Typography
                                variant="caption"
                                sx={{ color: "#0ea5e9", fontWeight: 600 }}
                              >
                                {ePercent}%
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#f59e0b", fontWeight: 600 }}
                              >
                                {gPercent}%
                              </Typography>
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                    
                  </Stack>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      p: 2,
                      bgcolor: "rgba(25, 118, 210, 0.08)",
                      width: 200,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "rgba(25, 118, 210, 0.2)",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", lineHeight: 1.2 }}
                      >
                        CO₂ Price
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#10b981" }}
                      >
                        €{co2Price} / t CO₂
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <ReactApexChart
                    options={createChartConfig().marketShareChartOptions}
                    series={createChartConfig().marketShareChartSeries}
                    type="line"
                    height={isSmall ? 260 : 350}
                  />
                </Box>

               
                <Box
                  sx={{
                    mt: 4,
                    p: 3,
                    bgcolor: "background.paper",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center">
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor:
                            currentAdvice === "e-boiler"
                              ? "rgba(25, 118, 210, 0.1)"
                              : "rgba(46, 125, 50, 0.1)",
                          mr: 2,
                        }}
                      >
                        <InfoOutlinedIcon
                          sx={{
                            color:
                              currentAdvice === "e-boiler"
                                ? "#1976d2"
                                : "#2e7d32",
                            fontSize: 24,
                          }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          Current Hour Recommendation
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Use{" "}
                          {currentAdvice === "e-boiler"
                            ? "E-Boiler"
                            : "Gas Boiler"}
                        </Typography>
                      </Box>
                    </Box>
                   
                  </Box>
                </Box>

                
              </Box>
            )}

            {/* Tab 2: API Configuration */}
            {activeTab === 1 && (
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={3}
                >
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: "rgba(124, 77, 255, 0.1)",
                        mr: 2,
                      }}
                    >
                      <span
                        role="img"
                        aria-label="api"
                        style={{ fontSize: "24px" }}
                      >
                        📦
                      </span>
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        API Configuration
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Access your integration endpoints and credentials
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Quick Access Content */}
                <Box>
                  <Grid container spacing={3} mb={3}>
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          height: "200px",
                          display: "flex",
                          flexDirection: "column",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: "0 8px 25px rgba(124, 77, 255, 0.15)",
                            transform: "translateY(-4px)",
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Box display="flex" alignItems="center" mb={2}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: "rgba(25, 118, 210, 0.1)",
                                mr: 2,
                              }}
                            >
                              <span
                                role="img"
                                aria-label="url"
                                style={{ fontSize: "20px" }}
                              >
                                🔗
                              </span>
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, color: "#0ea5e9" }}
                            >
                              API Endpoint
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2, flex: 1 }}
                          >
                            Your unique API endpoint for integrations
                          </Typography>
                          <Box
                            sx={{
                              bgcolor: "rgba(25, 118, 210, 0.05)",
                              p: 2,
                              borderRadius: 1,
                              border: "1px solid rgba(25, 118, 210, 0.1)",
                              fontFamily: "monospace",
                              fontSize: "0.875rem",
                              color: "#0ea5e9",
                              wordBreak: "break-all",
                            }}
                          >
                            {apiUrl ? apiUrl : "Loading..."}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          height: "200px",
                          display: "flex",
                          flexDirection: "column",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: "0 8px 25px rgba(46, 125, 50, 0.15)",
                            transform: "translateY(-4px)",
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Box display="flex" alignItems="center" mb={2}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: "rgba(16, 185, 129, 0.1)",
                                mr: 2,
                              }}
                            >
                              <span
                                role="img"
                                aria-label="key"
                                style={{ fontSize: "20px" }}
                              >
                                🔑
                              </span>
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, color: "#10b981" }}
                            >
                              API Key
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2, flex: 1 }}
                          >
                            Your secure authentication key
                          </Typography>
                          <Box
                            sx={{
                              bgcolor: "rgba(46, 125, 50, 0.05)",
                              p: 2,
                              borderRadius: 1,
                              border: "1px solid rgba(46, 125, 50, 0.1)",
                              fontFamily: "monospace",
                              fontSize: "0.875rem",
                              color: "#10b981",
                              wordBreak: "break-all",
                            }}
                          >
                            {apiKey || "Not configured"}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  {/* Settings Payload Template */}
                  <Box sx={{ mt: 4, mb: 4 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={3}
                    >
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "rgba(46, 125, 50, 0.1)",
                            mr: 2,
                          }}
                        >
                          <span
                            role="img"
                            aria-label="settings"
                            style={{ fontSize: "24px" }}
                          >
                            ⚙️
                          </span>
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 600, mb: 0.5 }}
                          >
                            Settings Payload Template
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            JSON template for API configuration with current
                            settings
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        p: 3,
                        bgcolor: "rgba(248, 249, 250, 0.8)",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        position: "relative",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          Settings JSON Payload
                        </Typography>
                        <Tooltip
                          title={
                            copiedSettings ? "Copied!" : "Copy to Clipboard"
                          }
                        >
                          <IconButton
                            onClick={handleCopySettings}
                            size="small"
                            sx={{
                              color: copiedSettings ? "#10b981" : "#666",
                              "&:hover": {
                                bgcolor: "rgba(16, 185, 129, 0.1)",
                              },
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: "background.paper",
                          p: 2,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          maxHeight: "300px",
                          overflow: "auto",
                          "&::-webkit-scrollbar": {
                            width: "8px",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "#f1f1f1",
                            borderRadius: "4px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#c1c1c1",
                            borderRadius: "4px",
                            "&:hover": {
                              backgroundColor: "#a8a8a8",
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="body1"
                          component="pre"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            color: "#2e7d32",
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {console.log(
                            "dfdd:",
                            country,
                            gasPriceType,
                            gasPrice,
                            co2PriceType,
                            co2Price,
                            eBoilerEff,
                            gasBoilerEff,
                            gridCost,
                            userInfo?.email
                          )}
                          {/* dfdd: BE fixed 35 live 0.5 95 90 0.12 user@example.com */}
                          {JSON.stringify(
                            {
                              country: country,
                              gas_price: {
                                mode: gasPriceType,
                                value:
                                  gasPriceType === "fixed"
                                    ? parseFloat(gasPrice) || 75
                                    : null,
                              },
                              co2_price: {
                                mode: co2PriceType,
                                value: parseFloat(co2Price),
                              },
                              e_boiler_efficiency: parseFloat(eBoilerEff) || 95,
                              gas_boiler_efficiency:
                                parseFloat(gasBoilerEff) || 90,
                              grid_cost: parseFloat(gridCost) || 0.12,
                              email:
                                localStorage.getItem("userrealemail") ||
                                "user@example.com",
                            },
                            null,
                            2
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="center" mb={3} gap={2}>
                    <Button
                      variant="contained"
                      onClick={handleMakeApiRequest}
                      disabled={isLoadingRequest || !apiUrl || !apiKey}
                      sx={{
                        bgcolor: "#7c4dff",
                        color: "white",
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "1rem",
                        boxShadow: "0 4px 14px rgba(124, 77, 255, 0.3)",
                        "&:hover": {
                          bgcolor: "#6a3dff",
                          boxShadow: "0 6px 20px rgba(124, 77, 255, 0.4)",
                          transform: "translateY(-2px)",
                        },
                        "&:disabled": {
                          bgcolor: "rgba(124, 77, 255, 0.3)",
                          color: "rgba(255, 255, 255, 0.7)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {isLoadingRequest ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={20} sx={{ color: "white" }} />
                          Testing API...
                        </Box>
                      ) : (
                        "Test API Request"
                      )}
                    </Button>

                  </Box>
                 

                  {(apiResponse || requestError) && (
                    <Card
                      sx={{
                        mt: 3,
                        bgcolor: requestError
                          ? "rgba(211, 47, 47, 0.02)"
                          : "rgba(46, 125, 50, 0.02)",
                        border: `2px solid ${
                          requestError
                            ? "rgba(211, 47, 47, 0.1)"
                            : "rgba(46, 125, 50, 0.1)"
                        }`,
                        borderRadius: 3,
                        overflow: "hidden",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: requestError
                            ? "0 8px 25px rgba(211, 47, 47, 0.15)"
                            : "0 8px 25px rgba(46, 125, 50, 0.15)",
                        },
                      }}
                    >
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 1,
                              bgcolor: requestError
                                ? "rgba(211, 47, 47, 0.1)"
                                : "rgba(46, 125, 50, 0.1)",
                              mr: 2,
                            }}
                          >
                            <span
                              role="img"
                              aria-label="response"
                              style={{ fontSize: "20px" }}
                            >
                              {requestError ? "❌" : "✅"}
                            </span>
                          </Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: requestError ? "#d32f2f" : "#2e7d32",
                            }}
                          >
                            {requestError ? "Request Failed" : "API Response"}
                          </Typography>
                        </Box>

                        {requestError ? (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {requestError}
                          </Alert>
                        ) : (
                          <Box sx={{ mb: 2 }}>
                            <Box display="flex" gap={2} mb={2}>
                              <Box
                                sx={{
                                  px: 2,
                                  py: 1,
                                  bgcolor: "rgba(16, 185, 129, 0.1)",
                                  borderRadius: 1,
                                  border: "1px solid rgba(46, 125, 50, 0.2)",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Status
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600, color: "#10b981" }}
                                >
                                  {apiResponse.status} {apiResponse.statusText}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  px: 2,
                                  py: 1,
                                  bgcolor: "rgba(16, 185, 129, 0.1)",
                                  borderRadius: 1,
                                  border: "1px solid rgba(46, 125, 50, 0.2)",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Timestamp
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 600, color: "#10b981" }}
                                >
                                  {new Date(
                                    apiResponse.timestamp
                                  ).toLocaleString()}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}

                        <Box
                          sx={{
                            bgcolor: "rgba(0, 0, 0, 0.02)",
                            p: 3,
                            borderRadius: 2,
                            border: "1px solid rgba(0, 0, 0, 0.05)",
                            maxHeight: "400px",
                            overflow: "auto",
                            "&::-webkit-scrollbar": {
                              width: "8px",
                            },
                            "&::-webkit-scrollbar-track": {
                              backgroundColor: "#f1f1f1",
                              borderRadius: "4px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#c1c1c1",
                              borderRadius: "4px",
                              "&:hover": {
                                backgroundColor: "#a8a8a8",
                              },
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            component="pre"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.875rem",
                              color: requestError ? "#d32f2f" : "#2e7d32",
                              margin: 0,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              lineHeight: 1.6,
                            }}
                          >
                            {requestError
                              ? requestError
                              : JSON.stringify(apiResponse.data, null, 2)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  <Box
                    sx={{
                      mt: 3,
                      p: 3,
                      bgcolor: "rgba(124, 77, 255, 0.05)",
                      borderRadius: 3,
                      border: "1px solid rgba(124, 77, 255, 0.1)",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={2}
                      mb={1}
                    >
                      <InfoOutlinedIcon
                        sx={{ color: "#7c4dff", fontSize: 24 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, color: "#7c4dff" }}
                      >
                        Ready to Integrate
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Your API is configured and ready for external
                      integrations. Use the endpoint and key above to connect
                      your applications.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </SectionPaper>


        </Container>
      </Main>
    </Box>
  );
};

export default LeadgenDashboard;
